import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, ArrowLeft, Check, X, ToggleLeft, ToggleRight, 
  Trash2, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnterprisesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [connections, setConnections] = useState({
    pending: [],
    active: [],
    suspended: [],
    rejected: [],
    invitations: []
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/driver/connections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setConnections(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleRespond = async (connectionId, accept) => {
    setActionLoading(connectionId);
    try {
      const response = await fetch(`${API_URL}/api/driver/connections/${connectionId}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accept })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchConnections();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggle = async (connectionId, isActive) => {
    setActionLoading(connectionId);
    try {
      const response = await fetch(`${API_URL}/api/driver/connections/${connectionId}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: isActive })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchConnections();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (connectionId, companyName) => {
    if (!window.confirm(`Supprimer definitivement la connexion avec ${companyName}?`)) {
      return;
    }
    
    setActionLoading(connectionId);
    try {
      const response = await fetch(`${API_URL}/api/driver/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchConnections();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const ConnectionCard = ({ conn, type }) => {
    const isLoading = actionLoading === conn.id;
    
    return (
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700" data-testid={`connection-${conn.id}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">{conn.company_name}</h3>
              <p className="text-sm text-slate-400">Invite par {conn.admin_name}</p>
            </div>
          </div>
          
          {type === 'active' && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs">
              <CheckCircle className="w-3 h-3" />
              Actif
            </div>
          )}
          {type === 'suspended' && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs">
              <AlertCircle className="w-3 h-3" />
              Suspendu
            </div>
          )}
          {type === 'pending' && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs">
              <Clock className="w-3 h-3" />
              En attente
            </div>
          )}
          {type === 'rejected' && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs">
              <XCircle className="w-3 h-3" />
              Refuse
            </div>
          )}
        </div>
        
        <p className="text-xs text-slate-500 mb-3">
          Invitation recue le {formatDate(conn.invited_at || conn.created_at)}
        </p>
        
        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
          {type === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => handleRespond(conn.id, true)}
                disabled={isLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid={`accept-${conn.id}`}
              >
                {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Accepter
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRespond(conn.id, false)}
                disabled={isLoading}
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
                data-testid={`reject-${conn.id}`}
              >
                <X className="w-4 h-4 mr-1" />
                Refuser
              </Button>
            </>
          )}
          
          {(type === 'active' || type === 'suspended') && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleToggle(conn.id, type === 'suspended')}
                disabled={isLoading}
                className={`flex-1 ${type === 'active' ? 'border-amber-500/50 text-amber-400' : 'border-green-500/50 text-green-400'}`}
                data-testid={`toggle-${conn.id}`}
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : type === 'active' ? (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-1" />
                    Suspendre l'acces
                  </>
                ) : (
                  <>
                    <ToggleRight className="w-4 h-4 mr-1" />
                    Reactiver l'acces
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRemove(conn.id, conn.company_name)}
                disabled={isLoading}
                className="border-red-500/50 text-red-400 hover:bg-red-500/20 px-3"
                data-testid={`remove-${conn.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const hasPending = connections.pending.length > 0;
  const hasActive = connections.active.length > 0;
  const hasSuspended = connections.suspended.length > 0;
  const hasAny = hasPending || hasActive || hasSuspended || connections.rejected.length > 0;

  return (
    <div className="min-h-screen bg-slate-950 pb-24">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10 px-4 py-4 border-b border-slate-800">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Mes Entreprises</h1>
            <p className="text-sm text-slate-400">Gerez vos connexions avec les entreprises</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Pending Invitations */}
        {hasPending && (
          <section>
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Invitations en attente ({connections.pending.length})
            </h2>
            <div className="space-y-3">
              {connections.pending.map(conn => (
                <ConnectionCard key={conn.id} conn={conn} type="pending" />
              ))}
            </div>
          </section>
        )}

        {/* Active Connections */}
        {hasActive && (
          <section>
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Connexions actives ({connections.active.length})
            </h2>
            <p className="text-sm text-slate-400 mb-3">
              Ces entreprises ont acces a toutes vos donnees de conduite.
            </p>
            <div className="space-y-3">
              {connections.active.map(conn => (
                <ConnectionCard key={conn.id} conn={conn} type="active" />
              ))}
            </div>
          </section>
        )}

        {/* Suspended Connections */}
        {hasSuspended && (
          <section>
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              Acces suspendus ({connections.suspended.length})
            </h2>
            <p className="text-sm text-slate-400 mb-3">
              Ces entreprises n'ont plus acces a vos donnees temporairement.
            </p>
            <div className="space-y-3">
              {connections.suspended.map(conn => (
                <ConnectionCard key={conn.id} conn={conn} type="suspended" />
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {!hasAny && (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Aucune connexion</h3>
            <p className="text-slate-400 text-sm max-w-xs mx-auto">
              Vous n'etes connecte a aucune entreprise. 
              Quand un administrateur vous invitera, l'invitation apparaitra ici.
            </p>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700">
          <h4 className="font-semibold text-white mb-2">Comment ca fonctionne?</h4>
          <ul className="text-sm text-slate-400 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Les entreprises peuvent vous envoyer une invitation par email
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Vous choisissez d'accepter ou de refuser chaque invitation
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Vous pouvez suspendre temporairement l'acces a tout moment
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Supprimer une connexion retire definitivement l'acces
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EnterprisesPage;
